import BilanCompta from "./BilanCompta";
import { useState, useEffect } from "react";

const API = "https://surabaya-api.onrender.com/api";

const STATUS_COLORS = {
  ok:        { bg: "var(--color-background-success)", text: "var(--color-text-success)", label: "OK" },
  alerte:    { bg: "var(--color-background-danger)",  text: "var(--color-text-danger)",  label: "Alerte" },
  incomplet: { bg: "var(--color-background-warning)", text: "var(--color-text-warning)", label: "Incomplet" },
};

const Badge = ({ status }) => {
  const s = STATUS_COLORS[status] || STATUS_COLORS.ok;
  return <span style={{ background: s.bg, color: s.text, fontSize: 11, fontWeight: 500, padding: "2px 8px", borderRadius: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</span>;
};

const Card = ({ children, style = {} }) => (
  <div style={{ background: "var(--color-background-primary)", border: "0.5px solid var(--color-border-tertiary)", borderRadius: 12, padding: "1rem 1.25rem", ...style }}>{children}</div>
);

const MetricCard = ({ label, value, sub, color }) => (
  <div style={{ background: "var(--color-background-secondary)", borderRadius: 8, padding: "1rem", display: "flex", flexDirection: "column", gap: 4 }}>
    <span style={{ fontSize: 11, color: "var(--color-text-secondary)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</span>
    <span style={{ fontSize: 28, fontWeight: 500, color: color || "var(--color-text-primary)", lineHeight: 1 }}>{value}</span>
    {sub && <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{sub}</span>}
  </div>
);

const SectionTitle = ({ children }) => (
  <p style={{ fontSize: 11, fontWeight: 500, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", margin: "0 0 12px" }}>{children}</p>
);

const TYPE_LABELS = { restaurant: "Resto", caleche: "Calèche", caleche_bateau: "Cal.+Bateau", journee_complete: "Journée", fondue: "Fondue" };

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(new Date());
  const [ventes, setVentes] = useState(null);
  const [taches, setTaches] = useState([]);
  const [bilan, setBilan] = useState(null);
  const [onglet, setOnglet] = useState("jour");

  const load = () => {
    setLoading(true);
    fetch(`${API}/dashboard/ventes`).then(r=>r.json()).then(setVentes).catch(()=>{});
    fetch(`${API}/patrick/taches`).then(r=>r.json()).then(d=>setTaches(Array.isArray(d)?d:[])).catch(()=>{});
    fetch(`${API}/dashboard/bilan`).then(r=>r.json()).then(setBilan).catch(()=>{});
    fetch(`${API}/dashboard`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => {
    load();
    const tick = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(tick);
  }, []);

  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });
  const heureStr = now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });

  if (loading) return <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "60vh", color: "var(--color-text-secondary)", fontSize: 14 }}>Chargement...</div>;
  if (error) return <div style={{ padding: "2rem", color: "var(--color-text-danger)", fontSize: 14 }}>Erreur : {error}</div>;

  const { reservations, haccp, stock } = data;
  const resasMidi = reservations.today.filter(r => r.heure < "15:00");
  const resasSoir = reservations.today.filter(r => r.heure >= "15:00");
  const totalCaleche = reservations.today
    .filter(r => ["caleche","caleche_bateau","journee_complete","fondue","caleche_fondue"].includes(r.type) && r.statut === "confirmee")
    .reduce((sum, r) => sum + (r.nb_personnes || 0), 0);

  const ResaRow = ({ r }) => (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderTop: "0.5px solid var(--color-border-tertiary)" }}>
      <div>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{r.heure} — {r.nom}</span>
        <span style={{ fontSize: 12, color: "var(--color-text-secondary)", marginLeft: 8 }}>{r.nb_personnes}p · {TYPE_LABELS[r.type] || r.type}</span>
      </div>
      <Badge status={r.statut === "confirmee" ? "ok" : "incomplet"} />
    </div>
  );

  return (
    <div style={{ padding: "1.5rem", maxWidth: 900, margin: "0 auto" }}>
      {/* Onglets */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1.5rem" }}>
        {[["jour", "Aujourd'hui"], ["bilan", "Bilan semaine"], ["compta", "Comptabilité"]].map(([k, l]) => (
          <button key={k} onClick={() => setOnglet(k)} style={{ fontSize: 13, padding: "6px 16px", borderRadius: 20, border: `0.5px solid ${onglet===k ? "var(--color-text-primary)" : "var(--color-border-tertiary)"}`, background: onglet===k ? "var(--color-text-primary)" : "transparent", color: onglet===k ? "var(--color-background-primary)" : "var(--color-text-secondary)", cursor: "pointer" }}>{l}</button>
        ))}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.5rem" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 500, margin: 0, textTransform: "capitalize" }}>{dateStr}</h1>
          <p style={{ fontSize: 14, color: "var(--color-text-secondary)", margin: "4px 0 0" }}>{heureStr} — Dashboard opérationnel</p>
        </div>
        <button onClick={load} style={{ fontSize: 13, padding: "6px 14px" }}>Actualiser</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 12, marginBottom: "1.5rem" }}>
        <MetricCard label="Couverts resto" value={reservations.couverts_restaurant} sub="aujourd'hui" />
        <MetricCard label="Calèche / excursions" value={totalCaleche} sub="personnes" />
        <MetricCard label="HACCP" value={haccp.statut === "ok" ? "RAS" : haccp.statut === "alerte" ? "!" : "À compléter"}
          color={haccp.statut === "ok" ? "var(--color-text-success)" : haccp.statut === "alerte" ? "var(--color-text-danger)" : "var(--color-text-warning)"}
          sub={`${haccp.releves_aujourd_hui.length}/${haccp.frigos_total} frigos saisis`} />
        <MetricCard label="CA du jour" value={ventes ? ventes.ca_total.toFixed(2)+" €" : "..."} sub={ventes ? ventes.nb_ventes+" ticket(s)" : "chargement"} />
        <MetricCard label="Stock alertes" value={stock.alertes.length}
          color={stock.alertes.length > 0 ? "var(--color-text-danger)" : "var(--color-text-success)"}
          sub={stock.alertes.length === 0 ? "tout OK" : "sous le seuil"} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem", marginBottom: "1rem" }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <SectionTitle>Réservations aujourd'hui</SectionTitle>
            <span style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>{reservations.total_resas} au total</span>
          </div>
          {reservations.today.length === 0
            ? <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0 }}>Aucune réservation.</p>
            : <>
                {resasMidi.length > 0 && <><p style={{ fontSize: 11, color: "var(--color-text-secondary)", fontWeight: 500, margin: "0 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Midi</p>{resasMidi.map(r => <ResaRow key={r.id} r={r} />)}</>}
                {resasSoir.length > 0 && <><p style={{ fontSize: 11, color: "var(--color-text-secondary)", fontWeight: 500, margin: "12px 0 6px", textTransform: "uppercase", letterSpacing: "0.06em" }}>Soir</p>{resasSoir.map(r => <ResaRow key={r.id} r={r} />)}</>}
              </>
          }
        </Card>

        <Card>
          <SectionTitle>Semaine à venir</SectionTitle>
          {reservations.semaine.length === 0
            ? <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0 }}>Aucune réservation cette semaine.</p>
            : reservations.semaine.map(s => {
                const d = new Date(s.date instanceof Date ? s.date : s.date.toString().substring(0,10) + "T12:00:00");
                const label = d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" });
                return (
                  <div key={s.date} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: "0.5px solid var(--color-border-tertiary)" }}>
                    <span style={{ fontSize: 13, textTransform: "capitalize" }}>{label}</span>
                    <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>{s.nb_resas} résa{s.nb_resas > 1 ? "s" : ""} · {s.nb_personnes}p</span>
                  </div>
                );
              })
          }
        </Card>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <SectionTitle>HACCP températures</SectionTitle>
            <Badge status={haccp.statut} />
          </div>
          {haccp.frigos_sans_saisie.length > 0 && <>
            <p style={{ fontSize: 12, color: "var(--color-text-warning)", margin: "0 0 6px", fontWeight: 500 }}>Relevés manquants</p>
            {haccp.frigos_sans_saisie.map(f => <div key={f.id} style={{ fontSize: 13, padding: "4px 0", borderTop: "0.5px solid var(--color-border-tertiary)", color: "var(--color-text-warning)" }}>{f.nom}</div>)}
          </>}
          {haccp.releves_non_conformes.length > 0 && <>
            <p style={{ fontSize: 12, color: "var(--color-text-danger)", margin: "8px 0 6px", fontWeight: 500 }}>Non conformes</p>
            {haccp.releves_non_conformes.map((r, i) => <div key={i} style={{ fontSize: 13, padding: "4px 0", borderTop: "0.5px solid var(--color-border-tertiary)", color: "var(--color-text-danger)" }}>Frigo #{r.frigo_id} — {r.temperature}°C ({r.periode})</div>)}
          </>}
          {haccp.frigos_sans_saisie.length === 0 && haccp.releves_non_conformes.length === 0 &&
            <p style={{ fontSize: 13, color: "var(--color-text-success)", margin: 0 }}>Tous les frigos saisis et conformes.</p>}
        </Card>

        <Card>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <SectionTitle>Stock semi-finis</SectionTitle>
            <Badge status={stock.statut} />
          </div>
          {stock.composants.length === 0
            ? <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0 }}>Aucun composant configuré.</p>
            : stock.composants.map(c => {
                const qty = parseFloat(c.stock_actuel);
                const enAlerte = qty <= parseFloat(c.seuil_alerte || 0);
                return (
                  <div key={c.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: "0.5px solid var(--color-border-tertiary)" }}>
                    <span style={{ fontSize: 13, color: enAlerte ? "var(--color-text-danger)" : "var(--color-text-primary)" }}>{c.nom}</span>
                    <span style={{ fontSize: 13, fontWeight: 500, color: enAlerte ? "var(--color-text-danger)" : "var(--color-text-secondary)" }}>
                      {qty.toFixed(1)} {c.unite}
                      {enAlerte && <span style={{ marginLeft: 6, fontSize: 11, background: "var(--color-background-danger)", color: "var(--color-text-danger)", padding: "1px 6px", borderRadius: 4 }}>seuil</span>}
                    </span>
                  </div>
                );
              })
          }
        </Card>
      </div>
    <Card style={{ marginTop: "1rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <SectionTitle>Tâches équipe en cours</SectionTitle>
          <a href="?staff" style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>Gérer →</a>
        </div>
        {(() => {
          const actives = taches.filter(t => t.statut !== "termine");
          if (actives.length === 0) return <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0 }}>Aucune tâche en cours.</p>;
          const parPersonne = {};
          actives.forEach(t => { if (!parPersonne[t.assigne_a]) parPersonne[t.assigne_a] = []; parPersonne[t.assigne_a].push(t); });
          const emojis = { Patrick: "🔧", Christian: "👨‍🍳", Coralie: "🍽️", Mamat: "☕", Cocher: "🐴" };
          return Object.entries(parPersonne).map(([p, ts]) => (
            <div key={p} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: "0.5px solid var(--color-border-tertiary)" }}>
              <span style={{ fontSize: 13 }}>{emojis[p] || "👤"} {p}</span>
              <span style={{ fontSize: 13, color: ts.some(t=>t.priorite==="urgente") ? "var(--color-text-danger)" : "var(--color-text-secondary)" }}>
                {ts.length} tâche{ts.length > 1 ? "s" : ""}{ts.some(t=>t.priorite==="urgente") ? " 🚨" : ""}
              </span>
            </div>
          ));
        })()}
      </Card>
      {onglet === "bilan" && bilan && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12, marginBottom: "1.5rem" }}>
            <MetricCard label="CA semaine" value={bilan.ca_semaine !== null ? bilan.ca_semaine.toFixed(2) + " €" : "—"} sub="via Hiboutik" />
            <MetricCard label="Réservations" value={bilan.reservations.total} sub={bilan.reservations.couverts + " couverts"} />
            <MetricCard label="HACCP conformité" value={bilan.haccp.taux !== null ? bilan.haccp.taux + "%" : "—"} sub={bilan.haccp.conformes + "/" + bilan.haccp.total + " relevés"} color={bilan.haccp.taux !== null && bilan.haccp.taux < 90 ? "var(--color-text-danger)" : "var(--color-text-success)"} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>
            <Card>
              <SectionTitle>Résas par type</SectionTitle>
              {bilan.reservations.par_type.length === 0
                ? <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0 }}>Aucune résa cette semaine.</p>
                : bilan.reservations.par_type.map(r => (
                  <div key={r.type} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderTop: "0.5px solid var(--color-border-tertiary)" }}>
                    <span style={{ fontSize: 13 }}>{r.type}</span>
                    <span style={{ fontSize: 13, color: "var(--color-text-secondary)" }}>{r.nb} résa · {r.personnes}p</span>
                  </div>
                ))
              }
            </Card>
            <Card>
              <SectionTitle>Tâches équipe</SectionTitle>
              {bilan.taches.terminees.length > 0 && <>
                <p style={{ fontSize: 11, color: "var(--color-text-success)", fontWeight: 500, margin: "0 0 6px", textTransform: "uppercase" }}>Terminées cette semaine</p>
                {bilan.taches.terminees.map((t, i) => (
                  <div key={i} style={{ fontSize: 13, padding: "4px 0", borderTop: "0.5px solid var(--color-border-tertiary)", color: "var(--color-text-secondary)" }}>✅ {t.titre} ({t.assigne_a})</div>
                ))}
              </>}
              {bilan.taches.en_cours.length > 0 && <>
                <p style={{ fontSize: 11, color: "var(--color-text-warning)", fontWeight: 500, margin: "12px 0 6px", textTransform: "uppercase" }}>En cours</p>
                {bilan.taches.en_cours.map((t, i) => (
                  <div key={i} style={{ fontSize: 13, padding: "4px 0", borderTop: "0.5px solid var(--color-border-tertiary)", color: t.priorite === "urgente" ? "var(--color-text-danger)" : "var(--color-text-secondary)" }}>
                    {t.priorite === "urgente" ? "🚨" : "•"} {t.titre} ({t.assigne_a})
                  </div>
                ))}
              </>}
              {bilan.taches.terminees.length === 0 && bilan.taches.en_cours.length === 0 && <p style={{ fontSize: 13, color: "var(--color-text-secondary)", margin: 0 }}>Aucune tâche.</p>}
            </Card>
          </div>
        </div>
      )}
      {onglet === "compta" && <BilanCompta />}
    </div>
  );
}
